import { useEffect, useMemo, useRef, useState } from "react";
import { Button } from "@blueskydataplatform/shared-ui/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@blueskydataplatform/shared-ui/components/ui/card";
import { Input } from "@blueskydataplatform/shared-ui/components/ui/input";
import { toast } from "@blueskydataplatform/shared-ui/hooks/use-toast";
import { useSend2FA } from "../api/send-2fa";
import { useVerify2FA } from "../api/verify-2fa";
import useQueryParams from "@/hooks/use-query-params";
import { getSafeRedirect } from "@/lib/utils";
import Logo from "@/components/logo";
import { useGetSoftwareStatement } from "@/features/software-statement/api/get-software-statement";
import { AxiosError } from "axios";
import { clearStoredPromotionCode } from "@blueskydataplatform/shared-ui/store/use-order";
import { useMcpProvider } from "@/providers/mcp-providers-context";
import { completeMcpAuthCallbackForProvider, reportMcpFailure } from "../complete-mcp-auth-callback";
import McpCallbackForm, { type McpCallbackHandle } from "../mcp-callback-form";

const CODE_LENGTH = 6;

const Verification2FA = (props: { email: string; method: "phone" | "email" }) => {
  const [code, setCode] = useState<string[]>(Array(CODE_LENGTH).fill(""));
  const inputRefs = useRef<(HTMLInputElement | null)[]>([]);
  const { redirect, isFromMCP } = useQueryParams();
  const { provider } = useMcpProvider();
  const providerRef = useRef(provider);
  providerRef.current = provider;

  const send2FAMutation = useSend2FA({
    mutationConfig: {
      onSuccess: () => {
        toast.success("Code sent successfully, check your email for the code.");
      },
      onError: () => {
        toast.error("Failed to send code, please try again later.");
      },
    },
  });

  const callbackRef = useRef<McpCallbackHandle>(null);

  const softwareStatementMutation = useGetSoftwareStatement();
  const verify2FAMutation = useVerify2FA({
    mutationConfig: {
      onSuccess: async response => {
        const data = response.data;
        toast.success("Code verified successfully.");

        clearStoredPromotionCode();

        if (isFromMCP) {
          try {
            await completeMcpAuthCallbackForProvider({
              getProvider: () => providerRef.current,
              fetchSoftwareStatement: input => softwareStatementMutation.mutateAsync(input),
              callbackRef,
            });
          } catch (error) {
            reportMcpFailure(error, message => toast.error(message));
          }

          return;
        }

        // Only honor the `r` query param if it is a safe same-origin/relative
        // target — prevents open redirect to attacker-controlled domains (IH-004).
        const safeRedirect = getSafeRedirect(redirect);
        if (safeRedirect) {
          window.location.href = safeRedirect;
          return;
        }
        if (data.redirect) {
          window.location.href = data.redirect;
        }
      },
      onError: e => {
        const errorType = (e as AxiosError<{ error: string }>).response?.data?.error;
        if (errorType === "INVALID_CODE") {
          toast.error("Invalid code, please try again or resend the code to your email.");
          return;
        }
        if (errorType === "TOO_MANY_ATTEMPTS") {
          toast.error("Too many attempts. Please wait 15 minutes and log in again.");
          return;
        }
        toast.error("Failed to verify code, please try again later.");
      },
    },
  });
  const value = useMemo(() => code.join(""), [code]);
  const isComplete = value.length === CODE_LENGTH && code.every(c => c !== "");

  useEffect(() => {
    inputRefs.current[0]?.focus();
  }, []);

  const handleChange = (index: number, nextValue: string) => {
    // Accept only single digit 0-9
    const sanitized = nextValue.replace(/[^0-9]/g, "").slice(0, 1);
    const next = [...code];
    next[index] = sanitized;
    setCode(next);

    if (sanitized && index < CODE_LENGTH - 1) {
      inputRefs.current[index + 1]?.focus();
    }
  };

  const handleKeyDown = (index: number, e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Backspace" && !code[index] && index > 0) {
      const prevIndex = index - 1;
      inputRefs.current[prevIndex]?.focus();
    }
    if (e.key === "ArrowLeft" && index > 0) {
      inputRefs.current[index - 1]?.focus();
      e.preventDefault();
    }
    if (e.key === "ArrowRight" && index < CODE_LENGTH - 1) {
      inputRefs.current[index + 1]?.focus();
      e.preventDefault();
    }
  };

  const handlePaste = (e: React.ClipboardEvent<HTMLInputElement>) => {
    const pasted = e.clipboardData
      .getData("text")
      .replace(/[^0-9]/g, "")
      .slice(0, CODE_LENGTH);
    if (!pasted) return;
    e.preventDefault();
    const next = Array(CODE_LENGTH)
      .fill("")
      .map((_, i) => pasted[i] ?? "");
    setCode(next);
    const focusIndex = Math.min(pasted.length, CODE_LENGTH - 1);
    inputRefs.current[focusIndex]?.focus();
  };

  const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (!isComplete) return;
    verify2FAMutation.mutate({ data: { userCode: value } });
  };

  const handleResend = () => {
    send2FAMutation.mutate({ data: { email: props.email, method: props.method } });
  };

  return (
    <div className="min-h-screen flex items-center justify-center py-12 px-4 sm:px-6 lg:px-8">
      <Card className="w-full max-w-md">
        <CardHeader className="space-y-1">
          <div className="flex justify-center mb-2">
            <Logo className="h-8" />
          </div>
          <CardTitle className="text-2xl font-bold text-center">Two-step authentication</CardTitle>
          <CardDescription>To continue, please enter the 6-digit verification code sent to your email.</CardDescription>
          <div className="text-sm text-muted-foreground pt-4">
            Didn&apos;t receive code?{" "}
            <button type="button" onClick={handleResend} className="text-blue-600 hover:text-blue-500 font-medium">
              Resend code
            </button>
          </div>
        </CardHeader>
        <CardContent>
          <McpCallbackForm handleRef={callbackRef} />
          <form onSubmit={handleSubmit} className="space-y-6">
            <div className="flex items-center justify-between gap-2">
              {code.map((digit, i) => (
                <Input
                  key={i}
                  ref={el => {
                    inputRefs.current[i] = el;
                  }}
                  inputMode="numeric"
                  pattern="[0-9]*"
                  type="text"
                  maxLength={1}
                  className="h-14 w-12 text-center text-lg tracking-widest"
                  value={digit}
                  onChange={e => handleChange(i, e.target.value)}
                  onKeyDown={e => handleKeyDown(i, e)}
                  onPaste={handlePaste}
                />
              ))}
            </div>

            <Button type="submit" className="w-full" disabled={!isComplete || verify2FAMutation.isPending}>
              {verify2FAMutation.isPending ? "Verifying..." : "Continue"}
            </Button>
          </form>
        </CardContent>
      </Card>
    </div>
  );
};

export default Verification2FA;
